import { useEffect, useState } from "react";
import { getAsetMeta } from "../lib/asetApi";

// useAsetMeta — SATU kali muat GET /api/aset/meta per sesi, cache modul.
//
// L4/L6 (Owner, 16 Sep 2026): label dan rona status aset, status pengingat,
// kategori, bentuk jadwal, dan sembilan enum S1 hidup di
// backend/services/aset_meta.py — NOL label/warna ditulis di JSX. Halaman
// memakai `label(meta, "status_aset", kode)` dan `tone(...)`; `tone` adalah
// nama rona K73 apa adanya (success/warning/info/danger/neutral) yang
// StatusBadge terima langsung — bukan kosakata /meta/po (waiting/active/...).
//
// H7/H3/H1 (dan H3 laporan) berlabel TEMPLATE "{n}" — `labelPengingat()`
// mengisinya dari dua tanggal baris view, cermin `aset_meta.label_pengingat`.

let _meta = null;
let _inflight = null;

export function loadAsetMeta() {
  if (_meta) return Promise.resolve(_meta);
  if (!_inflight) {
    _inflight = getAsetMeta()
      .then((r) => { _meta = r.data; return _meta; })
      .catch((e) => { _inflight = null; throw e; });
  }
  return _inflight;
}

export function useAsetMeta() {
  const [meta, setMeta] = useState(_meta);
  const [galat, setGalat] = useState(null);
  useEffect(() => {
    let hidup = true;
    loadAsetMeta().then((m) => { if (hidup) setMeta(m); })
      .catch((e) => { if (hidup) setGalat(e); });
    return () => { hidup = false; };
  }, []);
  return { meta, galat, siap: !!meta };
}

function _butir(meta, daftar, kode) {
  return (meta?.[daftar] || []).find((b) => b.code === kode) || null;
}

/** Label dari meta; kode apa adanya kalau meta belum termuat / kode asing. */
export function label(meta, daftar, kode) {
  if (kode === null || kode === undefined || kode === "") return "—";
  return _butir(meta, daftar, kode)?.label || kode;
}

/** Rona K73 dari meta; "neutral" kalau nol punya tone. */
export function tone(meta, daftar, kode) {
  return _butir(meta, daftar, kode)?.tone || "neutral";
}

function _hari(a, b) {
  const da = new Date(String(a).slice(0, 10) + "T00:00:00Z");
  const db = new Date(String(b).slice(0, 10) + "T00:00:00Z");
  return Math.round((da - db) / 86400000);
}

/** L5: label status pengingat satu baris view jadwal. H7/H3/H1 dihitung dari
 *  tanggal_jatuh_tempo − tanggal_hari_ini_region (0 → label "hari ini"). */
export function labelPengingat(meta, baris) {
  const kode = baris?.status_pengingat;
  const butir = _butir(meta, "status_pengingat", kode);
  if (!butir) return kode || "—";
  const t = meta?.label_jatuh_tempo;
  if (!t?.berlaku_untuk?.includes(kode) || !baris?.tanggal_jatuh_tempo || !baris?.tanggal_hari_ini_region) {
    return butir.label;
  }
  const n = _hari(baris.tanggal_jatuh_tempo, baris.tanggal_hari_ini_region);
  return n <= 0 ? t.hari_ini : t.template.replace("{n}", String(n));
}

/** S1: label status pengingat LAPORAN satu baris view (H3 = akhir_bulan − hari ini). */
export function labelPengingatLaporan(meta, baris) {
  const kode = baris?.status_pengingat;
  const butir = _butir(meta, "status_pengingat_laporan", kode);
  if (!butir) return kode || "—";
  const t = meta?.label_batas_lapor;
  if (!t?.berlaku_untuk?.includes(kode) || !baris?.akhir_bulan || !baris?.tanggal_hari_ini_region) {
    return butir.label;
  }
  const n = _hari(baris.akhir_bulan, baris.tanggal_hari_ini_region);
  return n <= 0 ? t.hari_ini : t.template.replace("{n}", String(n));
}
